import { Button, ConfirmDialog, Paper, PaperBorderType } from '@roc/ui';
import { Grid, makeStyles } from '@material-ui/core';
import { observer } from 'mobx-react';
import { RetractPayoffDialog, usePayoffRequestStore } from '@roc/feature-loans';
import { RETRACTED_STAGE } from 'libs/feature-loans/src/loans/loanDetails/payoffs/constants/payoffs';
import { useState } from 'react';
import { GenerateCemaDialog, NotifyManagementApprovalDialog, PaReconciliationDialog, PayoffRequestDetailDialog, PrePayoffCaculatorDialog, PutOnHoldDialog, RequestRevisionsDialog } from '@roc/feature-payoffs';
import { useBaseStore } from '@roc/feature-app-core';
import { ApprovePayoffDialog } from '@roc/feature-payoffs';

const useStyles = makeStyles(() => ({
  buttonsContainer: {
    padding: '0 16px 16px 16px'
  },
  button: {
    height: '30px',
    padding: '4px 14px',
    textTransform: 'none',
  },
}));

const activeStatuses = ["Approved", "Approval & Forwarding Pending", "Pending Approval from Management", "Forwarded to Servicer", "On Hold"];

export const PayoffActionButtons = observer(({ loanId }) => {
  const classes = useStyles();
  const {
    payoffRequestStore,
    payoffRequestStore: { currentPayoff, selectedPayoff },
  } = usePayoffRequestStore();
  const [confirmAssistanceModalOpen, setConfirmAssistanceModalOpen] = useState(false);
  const [payoffRequestModalOpen, setPayoffRequestModalOpen] = useState(false);
  const [approvePayoffModalOpen, setApprovePayoffModalOpen] = useState(false);
  const [generateCemaModaOpen, setGenerateCemaModaOpen] = useState(false);
  const [generateCemaButtonDisabled, setGenerateCemaButtonDisabled] = useState(true);
  const payoffActive = activeStatuses.includes(selectedPayoff?.payoffStatus);
  const { globalStore } = useBaseStore();
  const [openRequestRevision, setOpenRequestRevision] = useState(false);
  const [openPutOnhold, setOpenPutOnhold] = useState(false);
  const [openNotifyManagementApproval, setOpenNotifyManagementApproval] = useState(false);
  const isOnHold = payoffRequestStore.selectedPayoff?.payoffStatus === 'On Hold';
  const [openPrePayoffCalculator, setOpenPrePayoffCalculator] = useState(false);
  const [openPaReconciliation, setOpenPaReconciliation] = useState(false);

  const buttons = [
    {
      label: 'Request Assistance',
      onClick: () => {
        openRequestAssistance();
      },
      show: payoffActive,
    },
    {
      label: 'Request Payoff',
      onClick: () => {
        openPayoffRequest();
      },
      show: true,
    },
    {
      label: 'Retract Payoff',
      onClick: () => {
        openRetractPayoff();
      },
      show: payoffActive,
    },
    {
      label: 'Approve Payoff',
      onClick: () => {
        openApprovePayoff();
      },
      show: payoffActive,
    },
    {
      label: 'Generate CEMA Documents',
      onClick: () => {
        openGenerateCema();
      },
      show: true,
      disabled: generateCemaButtonDisabled,
    },
    {
      label: 'Request Revisions',
      onClick: () => {
        setOpenRequestRevision(true);
      },
      show: payoffActive,
    },
    {
      label: 'Notify Management Approval',
      onClick: () => {
        setOpenNotifyManagementApproval(true);
      },
      show: payoffActive,
    },
    {
      label: !isOnHold ? 'Put on Hold' : 'Remove on Hold',
      onClick: () => {
        setOpenPutOnhold(true);
      },
      show: payoffActive,
    },
    {
      label: 'Payoff Calculator',
      onClick: () => {
        setOpenPrePayoffCalculator(true);
      },
      show: !payoffActive,
    },
    {
      label: 'PA Reconciliation',
      onClick: () => {
        setOpenPaReconciliation(true);
      },
      show: true,
    },
  ];

  const openLegacyCalulator = () => {
    window.parent.postMessage(
      { type: "LEGACY_CALCULATOR", loanId },
      '*'
    );
  }

  const openRequestAssistance = () => {
    setConfirmAssistanceModalOpen(true);
  };

  const handleCloseRequestAssistanceModal = () => {
    setConfirmAssistanceModalOpen(false);
  };

  const openPayoffRequest = () => {
    setPayoffRequestModalOpen(true);
  };

  const closePayoffRequest = () => {
    setPayoffRequestModalOpen(false);
  };

  const openApprovePayoff = () => {
    setApprovePayoffModalOpen(true);
  };

  const handleCloseApprovePayoffModal = () => {
    setApprovePayoffModalOpen(false);
  };

  const openGenerateCema = () => {
    setGenerateCemaModaOpen(true);
  };

  const handleCloseGenerateCemaModal = () => {
    setGenerateCemaModaOpen(false);
  };

  const openRetractPayoff = () => {
    if (currentPayoff.payoffStatus !== RETRACTED_STAGE)
      payoffRequestStore.setIsNewPayoffRetractModalOpen(true);
  };

  return (
    <>
      <Paper title='Actions' borderType={PaperBorderType.SOLID_BORDER}>
        <Grid container className={classes.buttonsContainer} direction='row' spacing={1}>
          {buttons.filter(item => item.show).map(({ label, onClick, disabled }) => (
            <Grid item key={label.replace(/\s+/g, '-').toLowerCase()}>
              <Button
                testId={label.replace(/\s+/g, '-').toLowerCase()}
                variant='contained'
                className={classes.button}
                onClick={onClick}
                color='primary'
                disabled={disabled || false}
              >
                {label}
              </Button>
            </Grid>
          ))
          }
        </Grid>
      </Paper>
      <ConfirmDialog
        open={confirmAssistanceModalOpen}
        confirmButtonText={'Yes'}
        handleClose={handleCloseRequestAssistanceModal}
        handleCancel={handleCloseRequestAssistanceModal}
        handleConfirm={() => {
          payoffRequestStore.requestPayoffAssistance(loanId, currentPayoff?.loanPayoffDataId);
          handleCloseRequestAssistanceModal();
        }}
        title={'Request Assistance'}
        body={'Are you sure you want to request assistance for this file?'}
      />
      <PayoffRequestDetailDialog loanId={loanId} open={payoffRequestModalOpen} onClose={closePayoffRequest} isEditMode={false} document={null} />
      <RetractPayoffDialog loanId={loanId} loanPayoffDataId={currentPayoff?.loanPayoffDataId} />
      <ApprovePayoffDialog loanId={loanId} open={approvePayoffModalOpen} onClose={handleCloseApprovePayoffModal} />
      <GenerateCemaDialog
        loanId={loanId}
        open={generateCemaModaOpen}
        onClose={handleCloseGenerateCemaModal}
        setButtonDisabled={setGenerateCemaButtonDisabled}
      />
      <RequestRevisionsDialog
        loanId={loanId}
        payoffId={null}
        open={openRequestRevision}
        onClose={() => setOpenRequestRevision(false)}
      />
      <PutOnHoldDialog
        loanId={loanId}
        payoffId={null}
        open={openPutOnhold}
        onClose={() => setOpenPutOnhold(false)}
      />
      <NotifyManagementApprovalDialog
        loanId={loanId}
        open={openNotifyManagementApproval}
        onClose={() => setOpenNotifyManagementApproval(false)}
      />
      <PrePayoffCaculatorDialog
        open={openPrePayoffCalculator}
        onCLose={() => setOpenPrePayoffCalculator(false)}
      />
      <PaReconciliationDialog
        loanId={loanId}
        open={openPaReconciliation}
        onClose={() => setOpenPaReconciliation(false)}
      />
    </>
  );
});
